// BarberBreaks.jsx
import React, { useState, useEffect } from 'react';
import BarberWeeklySchedule from './BarberWeeklySchedule';
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import useAuth from '../../../hooks/useAuth';
import { jwtDecode } from "jwt-decode";
import BarberMultipleDayBreak from "../BarberMultipleDayBreak";
import BarberBreakHandler from '../BarberBreakHandler';
import InfiniteScroll from 'react-infinite-scroll-component';

const BarberBreaks = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const axiosPrivate = useAxiosPrivate();
  const [appointments, setAppointments] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { auth } = useAuth();
  const decoded = auth?.token ? jwtDecode(auth.token) : undefined;
  const limit = 20; // items per page
  const [barberDays, setBarberDays] = useState('');
  const [notification, setNotification] = useState(""); // State for the notification message


  const apiUrl =
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_API_LOCAL
      : process.env.REACT_APP_API;

  useEffect(() => {
    fetchAppointments();
    fetchBarberDays();


    // eslint-disable-next-line
  }, []);

  const fetchAppointments = async (reset = false) => {
    try {
      const currentPage = reset ? 1 : page;

      const response = await axiosPrivate.get(
        `${apiUrl}/appointment-details/${decoded.id}?page=${currentPage}&limit=${limit}`
      );

      if (response.data.appointments.length === 0) {
        setHasMore(false);
        return;
      }

      if (reset) {
        setAppointments(response.data.appointments); // replace old appointments
        setPage(2); // next page will be 2
        setHasMore(response.data.appointments.length < response.data.totalCount);
      } else {
        setAppointments(prev => [...prev, ...response.data.appointments]);
        setPage(prev => prev + 1);

        if (appointments.length + response.data.appointments.length >= response.data.totalCount) {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError('Problem sa serverom, probaj Logout i Login. Pa zovi Darka :).');
    }
  };

    const handleBarberDaysChange = async (barberDays) => {
    axiosPrivate
    .put(`${apiUrl}/barbers/${decoded.id}`, { barberDays })
    .then(() => {
      setNotification(`Trenutno je moguće zakazati ${barberDays} dana unapred.`);
      setTimeout(() => setNotification(""), 10000);
    })
    .catch((error) => {
      console.error("Error updating availability:", error);
      setNotification("Problem sa serverom. Probaj Logout i Login, pa zovi Darka :).");
      setTimeout(() => setNotification(""), 10000);
    });
  
  };

    const fetchBarberDays = async () => {
    axiosPrivate
    .get(`${apiUrl}/barbers/${decoded.id}`)
    .then((response) => {
      setBarberDays(response.data.barberDays);
    })
    .catch((error) => {
      console.error("Error fetching barber details:", error);
      setError("Problem sa serverom, probaj Logout i Login. Pa zovi Darka :).");
    });
  
  }
  

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // helper functions
  const convertTime = (timeString) => {
    const timeParts = timeString.split(':');
    return timeParts.slice(0, 2).join(':');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleString('en-US', options);
  };

  const calculateEndTime = (startTime, durationInMinutes) => {
    const timeParts = startTime.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    const seconds = parseInt(timeParts[2], 10);

    const startDate = new Date();
    startDate.setHours(hours, minutes, seconds, 0);
    startDate.setMinutes(startDate.getMinutes() + durationInMinutes);

    const endHours = startDate.getHours().toString().padStart(2, '0');
    const endMinutes = startDate.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
  };

  const deleteAppointment = async (appointmentId) => {
    if (!appointmentId) {
      console.error('Invalid appointmentId:', appointmentId);
      setError('Invalid appointment ID');
      return;
    }

    try {
      const response = await axiosPrivate.delete(`${apiUrl}/appointment/${appointmentId}`);
      if (response.status !== 200) throw new Error('Failed to delete appointment');

      setAppointments((prev) =>
        prev.filter((appointment) => appointment.appointmentId !== appointmentId)
      );
    } catch (error) {
      console.error('Error deleting appointment:', error);
      setError('Problem sa serverom, probaj Logout i Login. Pa zovi Darka :).');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Fixed Header with Error Message */}
      {error && (
        <div className="sticky top-0 z-50 bg-red-600 text-white px-4 py-3 text-sm">
          <p className="text-center">{error}</p>
        </div>
      )}

      {/* Main Content with Mobile Padding */}
      <div className="w-full max-w-4xl mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6 overflow-x-hidden">
        {/* Weekly Schedule Manager */}
        <div className="w-full">
          <BarberWeeklySchedule 
            barberId={decoded?.id}
            apiUrl={apiUrl}
            axiosPrivate={axiosPrivate}
          />
        </div>

        {/* Break Handlers */}
        <div className="w-full space-y-4">
          <BarberBreakHandler
            setError={setError}
            fetchAllAppointmentsForBarber={fetchAppointments}
          />

          <BarberMultipleDayBreak
            setError={setError}
            fetchAllAppointmentsForBarber={fetchAppointments}
          />
        </div>

      <div className="w-full space-y-4">
        <h1 className="text-zinc-50 text-center">Promeni koliko dana unapred se može zakazati</h1>
        <div className="flex flex-col items-center w-full">
          <input
            type="number"
            placeholder={barberDays}
            className="p-2 m-2 border border-gray-500 rounded-xl text-black w-48 text-center"
            value={barberDays}
            onChange={(e) => setBarberDays(e.target.value)}
          />
          <button 
            className="w-48 py-2 my-4 bg-zinc-200 hover:bg-neutral-800 text-black hover:text-white rounded-xl font-bold"
            onClick={() => handleBarberDaysChange(barberDays)}
          >
            Promeni
          </button>
        </div>
      </div>
        
        
        {/* Back Button - Sticky on Mobile */}
        <div className="sticky bottom-0 left-0 right-0 bg-black pt-4 pb-4 sm:pb-6">
          <button
            onClick={() => navigate("/barber-dashboard")}
            className="w-full p-3 sm:p-4 bg-zinc-800 text-white text-sm sm:text-base rounded-xl sm:rounded-2xl shadow-lg hover:bg-zinc-700 active:bg-zinc-600 transition-colors touch-manipulation"
          >
            ← Nazad na Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default BarberBreaks;